/**
 * Account-based pension (ABP) projection helpers — minimum drawdown vs client nomination,
 * and marking pensions partially liquidated to fund acquisitions (RAD, village entry, etc.).
 * Ported verbatim from frontend/src/calculations/abpProjectionUtils.js.
 *
 * `markAbpFundedAcquisitionAfterLiquidation` is also duplicated (unchanged) into
 * frontend/src/strategy/StrategyIntegrationUtils.js's frontend copy — see the calculation-engine
 * migration plan for why (strategy/ stays client-side but calls this function).
 */

import { MINIMUM_PENSION_DRAWDOWN_RATES } from './constants/ratesAndThresholds.ts';
import { calculateAge } from './support/dateHelpers.ts';
import type { FactFind, FactFindAsset } from './factFindTypes.ts';

export function getMinimumPensionDrawdownRateForAge(ownerAge: number | null | undefined): number {
    if (ownerAge == null || (ownerAge as unknown as string) === '') return 0;
    const bracket = MINIMUM_PENSION_DRAWDOWN_RATES.find((b) => ownerAge <= b.age);
    return bracket ? bracket.rate : 0.14;
}

/** Legislative minimum annual payment ≈ balance × age-based factor (illustration). */
export function getAnnualMinimumAbpDraw(balance: number, ownerAge: number | null | undefined): number {
    return (balance || 0) * getMinimumPensionDrawdownRateForAge(ownerAge);
}

/**
 * How old the pension's owner is when this call is being made.
 *
 * Inside a PROJECTION the answer is already on the form data: birthdayPlugin seeds `__age` from the
 * run's anchor and advances it on each person's real birthday, so reading it is both cheaper and more
 * accurate than re-deriving one here. `yearIndex` is then ignored — it exists for the callers
 * that are NOT inside a projection (the strategy previews, which pass 1 and hold no `__age` at all),
 * where the age is still taken from the date of birth against today.
 */
export function getOwnerAgeForAbpAssetAtYearIndex(asset: FactFindAsset, factFind: FactFind, yearIndex = 1): number | null {
    // A partner-owned pension is assessed on the partner, unless the fact-find knows nothing about
    // them at all, in which case it falls back to the client — as it always has.
    const partner = factFind?.partner;
    const owner = asset.ownership === 'partner' && (partner?.__age != null || partner?.dob) ? partner : factFind?.client;
    if (owner?.__age != null) return owner.__age;
    if (owner?.dob) return calculateAge(owner.dob)! + (Math.max(1, yearIndex) - 1);
    return null;
}

/** After a partial/full liquidation to fund RAD, RV entry fee, etc.: ongoing draw = minimum only. */
export function markAbpFundedAcquisitionAfterLiquidation(asset: FactFindAsset, factFind: FactFind, yearIndex = 1): void {
    asset.__abpFundedAcquisition = true;
    const ownerAge = getOwnerAgeForAbpAssetAtYearIndex(asset, factFind, yearIndex);
    const bal = asset.value ?? 0;
    const minAnnual = getAnnualMinimumAbpDraw(bal, ownerAge);
    asset.income = bal > 0 ? minAnnual : 0;
}

/** Freeze client-stated annual draw for untouched ABPs. */
export function ensureAbpProjectionNominatedAnnualInitialized(asset: FactFindAsset): void {
    if (asset.__abpFundedAcquisition === true) return;
    if (asset.__abpProjectionNominatedAnnual == null) {
        asset.__abpProjectionNominatedAnnual = asset.income ?? 0;
    }
}

export function getAbpProjectionNominatedAnnual(asset: FactFindAsset): number {
    if (asset.__abpProjectionNominatedAnnual != null) {
        return asset.__abpProjectionNominatedAnnual;
    }
    return asset.income ?? 0;
}

export function initializeAbpProjectionNomineesOnFactFind(factFind: FactFind): void {
    (factFind.assets?.accountBasedPensions || []).forEach((asset) => {
        ensureAbpProjectionNominatedAnnualInitialized(asset);
    });
}

/** After any year-end balance change, refresh `asset.income` so the next `calculateFinancials` matches projection rules. */
export function syncAbpIncomeFieldAfterBalanceChange(asset: FactFindAsset, ownerAge: number | null): void {
    const bal = asset.value ?? 0;
    if (bal <= 0) {
        asset.income = 0;
        return;
    }
    if (asset.__abpFundedAcquisition === true) {
        asset.income = getAnnualMinimumAbpDraw(bal, ownerAge);
        return;
    }
    const nom = getAbpProjectionNominatedAnnual(asset);
    const minAnnual = getAnnualMinimumAbpDraw(bal, ownerAge);
    asset.income = Math.max(nom, minAnnual);
}

export function syncAllAbpIncomesForYear(factFind: FactFind, yearIndex: number): void {
    (factFind.assets?.accountBasedPensions || []).forEach((asset) => {
        const ownerAge = getOwnerAgeForAbpAssetAtYearIndex(asset, factFind, yearIndex);
        if (ownerAge == null) return;
        syncAbpIncomeFieldAfterBalanceChange(asset, ownerAge);
    });
}
